import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import { zodResolver } from '@hookform/resolvers/zod';
import Grid2 from '@mui/material/Unstable_Grid2';
import { type SubmitHandler, useForm } from 'react-hook-form';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import FormInput, {
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import { LeadFormTCPA } from '@/components/Form/LeadFormTCPA/LeadFormTCPA';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import {
  type LanderSubmitStepSchemaType,
  landerSubmitStepSchema,
} from '@/components/TopLevelComponents/PaidLander/PaidLander';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { MAX_IMAGE_WIDTH, setImgWidth } from '@/utils/frontify';

import useStyles from './SubmitStep.styles';

enum InputName {
  FULL_NAME = 'fullName',
  EMAIL = 'email',
  PHONE = 'phone',
  OWN_HOME = 'ownHome',
  // HONEYPOT FIELD
  ADDRESS = 'address',
}

export interface SubmitStepProps {
  heading?: string;
  subheading?: string;
  tcpa?: RichTextDocument;
  hideImageOnMobile?: boolean;
  image?: string;
  altText?: string;
  submitText?: string;
  showHomeownerQuestion?: boolean;
  isOnDark: boolean;
  submit: (data: LanderSubmitStepSchemaType) => void;
  show: boolean;
  fullNameLabel: string;
  emailLabel: string;
  phoneNumberLabel: string;
  reverseLayout?: boolean;
  fixedGrid?: boolean;
}

export default function SubmitStep({
  heading,
  subheading,
  tcpa,
  hideImageOnMobile = false,
  image,
  altText,
  submitText,
  isOnDark,
  submit,
  show = false,
  fullNameLabel,
  emailLabel,
  phoneNumberLabel,
  reverseLayout,
  fixedGrid,
}: SubmitStepProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LanderSubmitStepSchemaType>({
    resolver: zodResolver(landerSubmitStepSchema),
    mode: 'onTouched',
  });

  const { classes, cx } = useStyles({
    isOnDark,
    hideImageOnMobile,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const { isMobile } = useViewPortSize();
  const imageSize = isMobile ? 768 : 1440;

  const onSubmit: SubmitHandler<LanderSubmitStepSchemaType> = (
    data: LanderSubmitStepSchemaType
  ) => {
    submit(data);
  };

  return (
    <GridContainer2
      gridProps={{
        className: cx(
          show ? classes.visible : classes.hidden,
          reverseLayout ? classes.reverseLayout : classes.layout
        ),
      }}
      fixed={fixedGrid}
    >
      <Grid2
        xs={12}
        sm={9}
        md={6}
        className={cx(reverseLayout && classes.reverseContent)}
      >
        <div className={cx(globalClasses.chip, classes.step)}>step 2 of 2</div>
        <div
          className={cx(
            fixedGrid ? globalClasses.h2 : globalClasses.h1,
            classes.heading
          )}
        >
          {heading}
        </div>
        <div className={cx(globalClasses.body2, classes.subheading)}>
          {subheading}
        </div>
        <div className={classes.inputGroup}>
          <div className={classes.inputContainer}>
            <div className={classes.input}>
              <FormInput
                label={fullNameLabel}
                name={InputName.FULL_NAME}
                testId='full-name-input'
                type={InputType.TEXT}
                inputMode='text'
                isOnDark={isOnDark}
                error={errors[InputName.FULL_NAME]?.message}
                register={register}
              />
            </div>
          </div>
          <div className={cx(classes.inputContainer, classes.hidden)}>
            <div className={classes.input}>
              <FormInput
                aria-hidden='true'
                label='Address'
                name={InputName.ADDRESS}
                testId='address'
                type={InputType.TEXT}
                inputMode='text'
                isOnDark={isOnDark}
                error={errors[InputName.ADDRESS]?.message}
                register={register}
                autoComplete='off'
              />
            </div>
          </div>
          <div className={classes.inputContainer}>
            <div className={classes.input}>
              <FormInput
                label={emailLabel}
                name={InputName.EMAIL}
                testId='email-input'
                type={InputType.TEXT}
                inputMode='email'
                isOnDark={isOnDark}
                error={errors[InputName.EMAIL]?.message}
                register={register}
              />
            </div>
            <div className={classes.input}>
              <FormInput
                label={phoneNumberLabel}
                testId='phone-input'
                type={InputType.TEL}
                inputMode='tel'
                isOnDark={isOnDark}
                error={errors[InputName.PHONE]?.message}
                name={InputName.PHONE}
                register={register}
              />
            </div>
          </div>
        </div>
        {tcpa && <LeadFormTCPA text={tcpa} className={classes.tcpa} />}
        <Button
          testId='submit-button'
          type='submit'
          variant={ButtonVariant.PRIMARY}
          label={submitText}
          size={ButtonSize.MD}
          onClick={handleSubmit(onSubmit)}
          className={classes.button}
          disabled={Object.keys(errors).length > 0 || !isValid}
          isOnDark={isOnDark}
        />
      </Grid2>
      <Grid2 xs={12} md={6} className={classes.hideImageOnMobile}>
        {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
        <img
          src={setImgWidth(image, MAX_IMAGE_WIDTH)}
          className={classes.landerImage}
          alt={altText}
          width={imageSize}
        />
      </Grid2>
    </GridContainer2>
  );
}
